import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import "./button-gold.css";

const buttonVariants = cva(
  // NOTE: cursor-pointer is explicit because Tailwind v4 dropped the default
  // cursor: pointer on <button>. disabled:cursor-not-allowed pairs with the
  // disabled:pointer-events-none above so a disabled button shows the right
  // cursor on its parent box (pointer-events-none alone resolves the cursor
  // from the parent, which is usually wrong for a disabled control).
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        success:
          "bg-success text-success-foreground hover:bg-success/90 shadow-sm",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-muted",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
        // Tactile 3D-pushable variants. The button itself stays transparent
        // and unpadded; the visible surface is rendered as a 3-layer span
        // structure (see render block below). Styling lives in
        // button-gold.css; press animation is CSS-only.
        gold: "roost-gold-btn !h-auto !p-0 !rounded-[var(--roost-gold-radius)]",
        "gold-secondary": "roost-gold-btn !h-auto !p-0 !rounded-[var(--roost-gold-radius)]",
      },
      // Height scale is the single source of truth shared with the tactile
      // gold variants: these `h-*` values mirror the --roost-btn-h-* CSS vars
      // in button-gold.css (sm 36px / default 44px / lg 48px / xl 56px). A gold
      // `lg` and a default `lg` must be the same height. Keep both in lockstep.
      size: {
        sm: "h-9 px-3 text-xs",
        default: "h-11 px-4 py-2",
        lg: "h-12 px-6 text-base",
        xl: "h-14 px-8 text-base",
        icon: "h-11 w-11",
        "icon-sm": "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isGold = variant === "gold" || variant === "gold-secondary";

    const inner = loading ? (
      <>
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
        <span>Loading...</span>
      </>
    ) : (
      children
    );

    if (isGold && asChild && React.isValidElement(children)) {
      // asChild + gold: Radix Slot can't host the 3-layer span structure
      // (it expects a single mergeable child), so clone the child element
      // (e.g. a react-router <Link>) into the root and rebuild the layers
      // inside it. Keeps CTAs as real crawlable anchors.
      const child = children;
      return React.cloneElement(
        child,
        {
          className: cn(
            buttonVariants({ variant, size, className }),
            child.props.className,
          ),
          "data-variant": variant,
          "data-size": size || "default",
          "data-loading": loading || undefined,
          ref,
          ...props,
        },
        <>
          <span className="roost-gold-shadow" aria-hidden="true" />
          <span className="roost-gold-edge" aria-hidden="true" />
          <span className="roost-gold-front">{child.props.children}</span>
        </>,
      );
    }

    if (isGold) {
      // 3-layer pushable structure. Shadow + edge are decorative; the front
      // span carries content and drives the button's size via its padding.
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          data-variant={variant}
          data-size={size || "default"}
          disabled={disabled || loading}
          data-loading={loading || undefined}
          {...props}
        >
          <span className="roost-gold-shadow" aria-hidden="true" />
          <span className="roost-gold-edge" aria-hidden="true" />
          <span className="roost-gold-front">{inner}</span>
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        data-loading={loading || undefined}
        {...props}
      >
        {inner}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
